'use client';

import { useState } from 'react';

import { tx, asList } from '../_shared/coerce';
import { sectionInner } from '../_shared/styles';
import { Button, type ButtonVariant } from '@akademi/ui';
import { PortalLink, useCurrentUser, useBlockPart, BlockIcon } from '@akademi/site-builder-runtime';
import { resolvePaddingStyle, type PaddingBox } from '../_shared/resolvePadding';
import { BlockRoot } from '../_shared/BlockRoot';

type Localized = string | Record<string, string>;

export interface ButtonGroupItem {
  type?: 'button' | 'divider';
  title?: Localized;
  href?: string;
  /** Inline SVG markup (<svg>…</svg>) or an image URL. */
  icon?: string;
  buttonType?: ButtonVariant;
  itemAlign?: 'left' | 'center' | 'right';
}

export interface ButtonGroupProps {
  /** true → underline tab strip with active state.
   *  false → vertical stack of independent buttons/links. */
  tabMode?: boolean;
  sectionTitle?: Localized;
  sectionSubtitle?: Localized;
  items?: ButtonGroupItem[];
  /** Cap the number of rendered items. 0 / undefined = show all. */
  maxShow?: number;
  /** Fallback href for items that don't have their own href. */
  mainHref?: string;
  /** Stick the block to the top of the viewport while scrolling. */
  sticky?: boolean;
  /** Top offset in px when sticky is enabled (e.g. header height). Default 0. */
  stickyTop?: number;
  /** Alignment of icon+text inside every button. */
  itemAlign?: 'left' | 'center' | 'right';
  paddingOuter?: PaddingBox;
  paddingInner?: PaddingBox;
}

// ---------------------------------------------------------------------------
// tabMode=true: underline tab strip
// ---------------------------------------------------------------------------

function TabStrip({ items, mainHref }: { items: ButtonGroupItem[]; mainHref?: string }) {
  const [activeIdx, setActiveIdx] = useState(0);
  const part = useBlockPart();

  return (
    <div
      role="tablist"
      {...part('tablist')}
      style={{
        display: 'flex',
        gap: 'clamp(16px, 3vw, 24px)',
        borderBottom: '1px solid var(--color-border-default, var(--color-neutral-200, #e5e7eb))',
        overflowX: 'auto',
        scrollbarWidth: 'none',
      }}
    >
      {items.map((item, i) => {
        const isActive = i === activeIdx;
        const label = tx(item.title) || `Tab ${i + 1}`;
        const href = item.href ? mainHref + item.href : undefined;
        const tabStyle = {
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: 'var(--space-3, 12px) var(--space-1, 4px)',
          border: 'none',
          background: 'none',
          cursor: 'pointer',
          fontSize: 'clamp(13px, 1.7vw, 15px)',
          fontWeight: isActive ? 700 : 500,
          fontFamily: 'var(--font-family-body, inherit)',
          whiteSpace: 'nowrap' as const,
          textDecoration: 'none',
          color: isActive
            ? 'var(--color-brand-500, #319db3)'
            : 'var(--color-text-secondary, #6a6f73)',
          borderBottom: isActive
            ? '2px solid var(--color-brand-500, #319db3)'
            : '2px solid transparent',
          marginBottom: -1,
          transition: 'color 150ms ease, border-color 150ms ease',
        };
        const content = (
          <>
            {item.icon ? <BlockIcon value={item.icon} size={18} /> : null}
            {label}
          </>
        );
        if (href) {
          return (
            <PortalLink
              key={i}
              href={href}
              role="tab"
              aria-selected={isActive}
              style={tabStyle}
              {...part('tab', i)}
            >
              {content}
            </PortalLink>
          );
        }
        return (
          <button
            key={i}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => setActiveIdx(i)}
            style={tabStyle}
            {...part('tab', i)}
          >
            {content}
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// tabMode=false: vertical stack of Button links
// ---------------------------------------------------------------------------

const ALIGN_MAP = { left: 'flex-start', center: 'center', right: 'flex-end' } as const;

function VerticalButtons({
  items,
  mainHref,
  itemAlign = 'left',
}: {
  items: ButtonGroupItem[];
  mainHref?: string;
  itemAlign?: 'left' | 'center' | 'right';
}) {
  return (
    <div
      role="group"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        gap: 'clamp(6px, 1vw, 10px)',
      }}
    >
      {items.map((item, i) => {
        if (item.type === 'divider') {
          return (
            <hr
              key={i}
              style={{
                border: 'none',
                borderTop: '1px solid var(--color-border-default, #e5e7eb)',
                margin: 'clamp(2px, 0.5vw, 4px) 0',
              }}
            />
          );
        }

        const label = tx(item.title) || `Button ${i + 1}`;
        const variant: ButtonVariant = item.buttonType ?? 'default';
        const href = mainHref != null ? mainHref + (item.href ?? '') : item.href;
        const resolved = item.itemAlign ?? itemAlign;
        const btnStyle = { justifyContent: ALIGN_MAP[resolved], minWidth: 0 };
        const content = (
          <>
            {item.icon ? <BlockIcon value={item.icon} size={18} /> : null}
            {/* `flex` + `minWidth: 0` let this shrink below its text's natural
                width (flex items default to min-width: auto, which blocks
                ellipsis); without it a narrow column (e.g. a sidebar cell)
                just overflows the label instead of truncating it. */}
            <span
              style={{
                flex: '1 1 auto',
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {label}
            </span>
          </>
        );

        if (href) {
          return (
            <Button
              href={href}
              key={i}
              variant={variant}
              size="lg"
              style={{ width: '100%', ...btnStyle }}
            >
              {content}
            </Button>
          );
        }

        return (
          <Button key={i} variant={variant} size="lg" style={{ width: '100%', ...btnStyle }}>
            {content}
          </Button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Root block
// ---------------------------------------------------------------------------

export function ButtonGroup({
  tabMode = false,
  sectionTitle,
  sectionSubtitle,
  items,
  maxShow,
  mainHref,
  sticky = false,
  stickyTop = 0,
  itemAlign = 'left',
  paddingOuter,
  paddingInner,
}: ButtonGroupProps) {
  const user = useCurrentUser<{ isTrainer?: boolean }>();
  const allItems = asList<ButtonGroupItem>(items).filter(
    (item) => !(item.href?.includes('tab=teaching') && !user?.isTrainer),
  );
  const visible = maxShow && maxShow > 0 ? allItems.slice(0, maxShow) : allItems;

  if (visible.length === 0) return null;

  const title = tx(sectionTitle);
  const subtitle = tx(sectionSubtitle);

  const outerPad = resolvePaddingStyle(paddingOuter);
  const innerPad = resolvePaddingStyle(paddingInner);

  return (
    <BlockRoot
      as="section"
      style={{
        padding: 'var(--space-4, 16px) var(--space-4, 16px)',
        ...outerPad,
        ...(sticky
          ? {
              position: 'sticky',
              top: stickyTop,
              zIndex: 15,
              background: 'var(--color-surface-card, #fff)',
              borderRadius: 'var(--radius-lg, 8px)',
            }
          : {}),
      }}
    >
      <div style={{ ...sectionInner, ...innerPad }}>
        {title && (
          <h2
            style={{
              margin: 0,
              fontSize: 'clamp(20px, 3vw, 24px)',
              fontWeight: 600,
              color: 'var(--color-text-primary, #1c1d1f)',
              fontFamily: 'var(--font-family-display, inherit)',
            }}
          >
            {title}
          </h2>
        )}
        {subtitle && (
          <p
            style={{
              margin: 'var(--space-1-5, 6px) 0 0',
              fontSize: 'clamp(13px, 1.6vw, 14px)',
              color: 'var(--color-text-secondary, #6a6f73)',
            }}
          >
            {subtitle}
          </p>
        )}

        <div style={{ marginTop: title || subtitle ? 20 : 0 }}>
          {tabMode ? (
            <TabStrip items={visible} mainHref={mainHref} />
          ) : (
            <VerticalButtons items={visible} mainHref={mainHref} itemAlign={itemAlign} />
          )}
        </div>
      </div>
    </BlockRoot>
  );
}
